// One photo or voice note on a job — the shared media index (docs/SYNC.md §1, `media` → orgs/{org}/opsMedia).
// BattleOps (task photos, project photos) and BattleBids (site-assessment photos, voice notes) write the same
// collection, so a job's pictures are one list wherever you look. A doc is shared by its links: an assessment
// photo starts with `estimateId` only; hand-off stamps `projectId`; a task photo carries projectId + taskId.
//
//   id, kind: 'photo' | 'voice'
//   uri, thumbUri?          long-lived read links (the ops convention); a device path while still uploading
//   path, thumbPath?        orgs/{org}/ops/{projectId|_}/photos/{name}
//   contentType, size?, w?, h?, durationMs?
//   takenAt                 when it was taken (camera = now; a gallery pick = its EXIF date) — the sort key
//   at                      when it was added; `by` = uid (the server also stamps ownerUid)
//   source                  'task' | 'project' | 'assessment' | 'file'
//   projectId?, taskId?, tag? ('before' | 'progress' | 'after'), cover?      BattleOps links
//   estimateId?, itemId?, onProposal?                                           BattleBids links
//   caption?, transcript?, transcriptStatus? ('pending' | 'done' | 'failed')
// Pure.
import { PHOTO_TAGS } from './taskphotos.js';
import { isLocalUri } from './media.js';

export const MEDIA_KINDS = ['photo', 'voice'];
export const MEDIA_SOURCES = ['task', 'project', 'assessment', 'file'];
export const MEDIA_SOURCE_LABEL = { task: 'Task', project: 'Project', assessment: 'Assessment', file: 'File' };

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const num = (v) => (Number(v) > 0 ? Number(v) : null);

/**
 * A new media doc from a capture. `id` and `at` come from the caller (newId / now) so the doc is deterministic.
 * Required: id, uri, source. taskId implies source 'task'; estimateId with no project implies 'assessment'.
 */
export function newMediaDoc({
  id, uri, thumbUri = null, path = null, thumbPath = null, kind = 'photo', contentType = null, size = null, w = null, h = null, durationMs = null,
  takenAt = null, at, by = null, source = null, projectId = null, taskId = null, tag = null, cover = false,
  estimateId = null, itemId = null, onProposal = null, caption = null, transcript = null, transcriptStatus = null,
}) {
  if (!id || typeof uri !== 'string' || !uri) throw new Error('newMediaDoc: id and uri required');
  const k = MEDIA_KINDS.includes(kind) ? kind : 'photo';
  const src = MEDIA_SOURCES.includes(source) ? source : taskId ? 'task' : estimateId && !projectId ? 'assessment' : 'project';
  const added = num(at) || Date.now();
  const d = {
    id, kind: k, uri, path: str(path), contentType: str(contentType) || (k === 'voice' ? 'audio/mp4' : 'image/jpeg'),
    takenAt: num(takenAt) || added, at: added, source: src,
    projectId: str(projectId), taskId: str(taskId), estimateId: str(estimateId), itemId: str(itemId),
  };
  if (thumbUri) d.thumbUri = thumbUri;
  if (thumbPath) d.thumbPath = thumbPath;
  if (by) d.by = by;
  if (num(size)) d.size = num(size);
  if (num(w)) d.w = num(w);
  if (num(h)) d.h = num(h);
  if (num(durationMs)) d.durationMs = num(durationMs);
  if (k === 'photo') d.tag = PHOTO_TAGS.includes(tag) ? tag : (src === 'task' ? 'progress' : null);
  if (cover) d.cover = true;
  if (onProposal != null) d.onProposal = !!onProposal;
  if (str(caption)) d.caption = str(caption);
  if (k === 'voice') { if (str(transcript)) d.transcript = str(transcript); d.transcriptStatus = ['pending', 'done', 'failed'].includes(transcriptStatus) ? transcriptStatus : (str(transcript) ? 'done' : 'pending'); }
  return d;
}

/** Still on the phone it was taken on (the queue hasn't uploaded it yet). */
export const isPendingMedia = (m) => !!m && isLocalUri(m.uri);

/** The small picture for a tile — the thumb when one has landed, else the picture itself. */
export const thumbOf = (m) => (m && (m.thumbUri || m.uri)) || null;

/** Media docs for a task's new shots ([{ uri, tag, takenAt?, caption? }]) — one per shot, ids from `newId`. */
export function mediaFromShots(task, shots, { newId, now = Date.now(), by = null, cover = false } = {}) {
  if (!task?.id || typeof newId !== 'function') return [];
  return (shots || []).filter((s) => s && s.uri).map((s, i) => newMediaDoc({
    id: newId(), uri: s.uri, source: 'task', projectId: task.projectId || null, taskId: task.id, tag: s.tag || null,
    takenAt: s.takenAt || null, at: now + i, by, caption: s.caption || null, cover: cover && i === 0,
  }));
}
